import { listify } from "radash";

export const MethodsOfId = {
  KNOWN: "Personally Known",
  KBA: "KBA",
  WITNESS: "Credible Witness",
} as const;

export const methodsOfIdList = computed(() => {
  return listify(MethodsOfId, (key, value) => ({ value: key, label: value }));
});

export const AptType = {
  NOTARY: "Notary",
  PRIVATE: "Private",
  NOTARY_PRIVATE: "Notary and Private",
} as const;

export const aptTypes = computed(() => {
  return listify(AptType, (key, value) => ({ value: key, label: value }));
});

export const NotarialType = {
  Acknowledgment: "Acknowledgment",
  Affidavit_Jurat: "Jurat / Affidavit",
  Verification: "Verification",
  Protest: "Protest",
  Oath: "Oath",
  Deposition: "Deposition of Witness",
  Copy: "Copy Certification",
  Signature: "Signature Witnessing",
  Contents: "Contents of Acknowledgment",
  Proof: "Proof of Identity",
  Other: "Other",
} as const;

export const notarialTypes = computed(() => {
  return listify(NotarialType, (key, value) => ({ value: key, label: value }));
});

export const SessionStatus = {
  SCHEDULED: "Scheduled",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  IN_PROGRESS: "In Progress",
  ARCHIVED: "Archived",
} as const;

export const SessionKeyStatus = {
  SCHEDULED: "SCHEDULED",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  IN_PROGRESS: "IN_PROGRESS",
  ARCHIVED: "ARCHIVED",
};

export const sessionStatuses = computed(() => {
  return listify(SessionStatus, (key, value) => ({ value: key, label: value }));
});

export const sessionEndedStatuses = ["COMPLETED", "ARCHIVED", "CANCELLED"];

export const Durations = {
  15: "15 min",
  30: "30 min",
  45: "45 min",
  60: "60 min",
  75: "75 min",
  90: "90 min",
  105: "105 min",
  120: "120 min",
} as const;

export const durationList = computed(() => {
  return listify(Durations, (key, value) => ({
    value: String(key),
    label: value,
  }));
});

export const DocStatus = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  COMPLETE: "Complete",
  ARCHIVED: "Archived",
} as const;

export const docStatuses = computed(() => {
  return listify(DocStatus, (key, value) => ({ value: key, label: value }));
});

export interface AddressDetails {
  address_one: string;
  address_two: string;
  city: string;
  state: string;
  postal_code: string;
}
export interface ClientDetails {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
  ssn: string;
}
export interface PrincipleForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
  ssn: string;
  auth_type: string;
  address_one: string;
  address_two?: string | null;
  city: string;
  state: string;
  postal_code: string;
}
export interface AssociateForm {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_one: string;
  address_two?: string | null;
  city: string;
  state: string;
  postal_code: string;
}

export interface WitnessesForm {
  first_name: string;
  middle_name: string;
  last_name: string;
  email: string;
  phone: string;
  dob: string;
  ssn: string;
  address_one: string;
  address_two?: string | null;
  city: string;
  state: string;
  postal_code: string;
}
export interface TeamMemberForm {
  user_id: string;
  is_host: boolean;
}

export interface KBAPivot {
  isFetching: boolean;
  kba_consent: boolean;
  identity_attempts: number;
  kba_identity_verified: string;
  stripe_identity_attempts: number;
  stripe_identity_verified: string;
  kba_block_datetime: string;
  token_verify: boolean;
  pivot: {
    auth_type: string;
    end_datetime: Date;
    id: string;
    identity_attempts: number;
    model_type: string;
    start_datetime: Date;
    view_doc: true;
    witness_id: null;
  };
}
export interface ClientDetailsWithKBA extends ClientDetails, KBAPivot {}

/**
 * Interface for document data used in document management
 */
export interface IDocumentData {
  id: string;
  custom_properties: {
    title: string;
    doc_date?: string;
    needs_notary?: boolean;
    multiple?: boolean;
    status?: string;
    user_name?: string;
    notarial_act?: string;
    notarial_acts?: Array<{
      notarial_act: string;
      clients: string[];
    }>;
    isLocking?: boolean;
    pspdfkit_document_id?: string;
  };
  created_at: string;
}

/**
 * Interface for document form data
 */
export interface IDocForm {
  title: string;
  doc_date: any;
  needs_notary: boolean;
  multiple: boolean;
  file: null;
  notarial_act?: string;
  other_notarial_act?: string;
  notarial_acts: {
    notarial_act: string;
    clients: string[];
  }[];
}

/**
 * Interface for session data used in document management
 */
export interface ISessionData {
  session_status?: string;
}
